import { useState, useCallback } from 'react';
import { Plus, Loader2, Trash2, ExternalLink, Upload, File, FileCheck, FileWarning, FileText } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog } from '../ui';
import { useApiEffect } from '../../hooks';
import type { ArquivoVeiculo, ArquivoVeiculoCreate, ArquivoVeiculoResumo, TipoArquivo } from '../../types';
import { formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS: { value: TipoArquivo; label: string; icon: typeof FileText }[] = [
  { value: 'CRLV', label: 'CRLV', icon: FileCheck },
  { value: 'LAUDO', label: 'Laudo', icon: FileCheck },
  { value: 'CONTRATO', label: 'Contrato', icon: FileText },
  { value: 'NOTA_FISCAL', label: 'Nota Fiscal', icon: FileText },
  { value: 'DUT', label: 'DUT', icon: File },
  { value: 'VISTORIA', label: 'Vistoria', icon: FileWarning },
  { value: 'OUTROS', label: 'Outros', icon: File },
];

const emptyForm: ArquivoVeiculoCreate = {
  tipo: 'OUTROS',
  nome: '',
  url: '',
  tamanho: undefined,
};

export function ArquivosVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showError, showApiError } = useNotification();

  const [arquivos, setArquivos] = useState<ArquivoVeiculo[]>([]);
  const [resumo, setResumo] = useState<ArquivoVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ArquivoVeiculoCreate>(emptyForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [arquivosRes, resumoRes] = await Promise.all([
        api.get<ArquivoVeiculo[]>(`/veiculos/${veiculoId}/arquivos`, { signal }),
        api.get<ArquivoVeiculoResumo>(`/veiculos/${veiculoId}/arquivos/resumo`, { signal }),
      ]);
      setArquivos(arquivosRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.url.trim()) {
      showError('Nome e URL são obrigatórios');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/veiculos/${veiculoId}/arquivos`, form);
      showSuccess('Arquivo cadastrado');
      setShowForm(false);
      setForm(emptyForm);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/veiculos/${veiculoId}/arquivos/${deletingId}`);
      showSuccess('Arquivo excluído');
      setShowDeleteDialog(false);
      setDeletingId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const getTipoIcon = (tipo: TipoArquivo) => {
    const found = TIPOS.find((t) => t.value === tipo);
    return found?.icon || File;
  };

  const getTipoLabel = (tipo: TipoArquivo) => {
    return TIPOS.find((t) => t.value === tipo)?.label || tipo;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Arquivos - ${veiculoNome}`} size="xl">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Total de arquivos:</span>
              <span className="font-semibold">{resumo.totalArquivos}</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              setShowForm(true);
              setForm(emptyForm);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Novo Arquivo
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as TipoArquivo })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome do arquivo"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL do Arquivo *</label>
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setForm(emptyForm);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : arquivos.length === 0 ? (
            <div className="text-center py-8">
              <Upload className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Nenhum arquivo cadastrado</p>
            </div>
          ) : (
            arquivos.map((arquivo) => {
              const TipoIcon = getTipoIcon(arquivo.tipo);
              return (
                <div key={arquivo.id} className="flex items-center justify-between p-4 bg-white border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <TipoIcon className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{arquivo.nome}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{getTipoLabel(arquivo.tipo)}</span>
                        <span>{formatDate(arquivo.dataCadastro)}</span>
                        {arquivo.usuarioNome && <span>por {arquivo.usuarioNome}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={arquivo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Abrir arquivo"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => {
                        setDeletingId(arquivo.id);
                        setShowDeleteDialog(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Arquivo"
        message="Tem certeza que deseja excluir este arquivo?"
        confirmText="Excluir"
      />
    </Modal>
  );
}
